import { useConfigFileManagedMessage } from '@/components/HOCs/ConfigFileManagedMessage';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { AppComponentProps } from '../../typescriptHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectManageableWorkflowSteps,
    selectAppPlatform,
    selectAppStoreType,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsWorkflowsCIStepEnabled,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectReleaseStepsByReleaseType,
    selectShouldHideNonBlockingSteps,
    selectStepGraph,
    selectWorkflowEnabledSteps,
    selectWorkflowId,
} from '@/selectors/selectors';
import { getReleaseTypeForNonBlockingSteps, isNonBlockingStepForReleaseType } from '@/helpers';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { useMdmPlatformOverride } from '@/helpers/mdmPlatformOverride';
import { StepConfigSettingsDrawer } from '@/components/stepConfigs/StepConfigSettingsDrawer';
import { WorkflowStepsSection } from './WorkflowStepsSection';
import { DestinationsSettings } from './DestinationsSettings';
import { AddStepModal } from './AddStepModal';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { EyeInvisibleOutlined, MenuOutlined, PlusOutlined } from '@ant-design/icons';
import { ReleaseStepType, ReleaseType, RunwayResponseType, SettingType, ConfigFileSection } from '@/types';
import { antGrey } from '../styles/globalStyles';
import { Flex } from 'antd/lib';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Switch from 'antd/lib/switch';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    DraggableGroupLabel,
    DraggableStepRow,
    GroupDragPreview,
    StepDragHandle,
    StepDragPreview,
    StepDropZone,
} from './ReorderStepGraph';
import { DraggedSteps, moveStepsInGraph, parseDropTargetId, stepGroupsFromGraph } from './reorderStepGraphHelpers';
import { WorkflowStepGraph } from '@/types/workflow';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import { WorkflowUpdatable } from '@/actions/workflow';
import { globalMessage } from '@/utils/globalMessage';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { createAppSettingsRouteWithSettingType, STEP_CONFIG_SETTINGS_QUERY_PARAM } from '@/helpers/routeHelpers';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

interface AddStepTarget {
    stepGroupIndex: number;
    archetypeGroupIndex: number;
    insertAsNewArchetypeGroup: boolean;
    lockedArchetype?: ReleaseStepType;
}

export const WorkflowManagementSettings: React.FC<AppComponentProps> = ({ appId, orgId }) => {
    const [selectedStepConfigId, setSelectedStepConfigId] = React.useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const [addStepTarget, setAddStepTarget] = React.useState<AddStepTarget | null>(null);
    const [reorderGraph, setReorderGraph] = React.useState<WorkflowStepGraph | null>(null);
    const [dragged, setDragged] = React.useState<DraggedSteps | null>(null);
    const [showHiddenSteps, setShowHiddenSteps] = React.useState(false);
    const [renamingGroupKey, setRenamingGroupKey] = React.useState<string | null>(null);
    const [sectionWidth, setSectionWidth] = React.useState(0);
    const stepsContainerRef = React.useRef<HTMLDivElement>(null);
    const history = useHistory();
    const location = useLocation();

    const onEditClicked = (stepConfigId: string) => {
        setSelectedStepConfigId(stepConfigId);
        setIsDrawerOpen(true);
    };

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowSteps = useSelector((state: RootState) => selectStepGraph(state, appId));
    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const manageableWorkflowSteps = useSelector((state: RootState) => selectManageableWorkflowSteps(state, appId));
    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appId),
    );
    const blockingConfigByReleaseType = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );
    const releaseStepsByReleaseType = useSelector((state: RootState) => selectReleaseStepsByReleaseType(state, appId));

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const stepConfigId = params.get(STEP_CONFIG_SETTINGS_QUERY_PARAM);
        if (!stepConfigId || !workflowSteps) return;

        const hasStep = workflowSteps.stepGroups.some((stepGroup) =>
            stepGroup.archetypeGroups.some((archetypeGroup) =>
                archetypeGroup.steps.some((step) => step.stepConfigId === stepConfigId),
            ),
        );
        if (hasStep) {
            setSelectedStepConfigId(stepConfigId);
            setIsDrawerOpen(true);
        }

        params.delete(STEP_CONFIG_SETTINGS_QUERY_PARAM);
        history.replace({
            pathname: location.pathname,
            search: params.toString(),
            hash: location.hash,
        });
    }, [history, location.hash, location.pathname, location.search, workflowSteps]);

    const mdmOverride = useMdmPlatformOverride(appId);
    const flightpathConfigFileMessage = useConfigFileManagedMessage(ConfigFileSection.Flightpath);
    const canRenameGroups =
        useUserCanPerformAction(RunwayUserAction.UAUpdateAppSettings) && !flightpathConfigFileMessage;

    const dragSensors = useSensors(
        // A small distance keeps a click on the handle from registering as a drag.
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    );

    const displayedGraph = reorderGraph ?? workflowSteps;
    const isDraggingStep = dragged?.kind === 'step';

    // The timeline hides a step when the app hides non-required steps and every release type that
    // runs it marks it non-blocking. Release types without the step never showed it anyway, so they
    // don't count either way.
    const isStepHiddenFromTimeline = React.useCallback(
        (archetype: ReleaseStepType) => {
            if (!shouldHideNonBlockingSteps) return false;

            const releaseTypesWithStep = Object.keys(releaseStepsByReleaseType).filter((releaseType) =>
                releaseStepsByReleaseType[releaseType].includes(archetype),
            );
            if (releaseTypesWithStep.length === 0) return false;

            return releaseTypesWithStep.every((releaseType) =>
                isNonBlockingStepForReleaseType(
                    blockingConfigByReleaseType[getReleaseTypeForNonBlockingSteps(releaseType as ReleaseType)],
                    archetype,
                ),
            );
        },
        [shouldHideNonBlockingSteps, releaseStepsByReleaseType, blockingConfigByReleaseType],
    );

    const hiddenStepCount = React.useMemo(
        () =>
            workflowSteps?.stepGroups.reduce(
                (count, stepGroup) =>
                    count +
                    stepGroup.archetypeGroups.filter((archetypeGroup) =>
                        isStepHiddenFromTimeline(archetypeGroup.archetype),
                    ).length,
                0,
            ) ?? 0,
        [workflowSteps, isStepHiddenFromTimeline],
    );

    // Reordering needs every group on screen, otherwise the drop targets skip positions.
    const areHiddenStepsVisible = showHiddenSteps || !!reorderGraph;

    const draggedStep = React.useMemo(() => {
        if (!isDraggingStep || !displayedGraph) return null;

        for (const stepGroup of displayedGraph.stepGroups) {
            for (const archetypeGroup of stepGroup.archetypeGroups) {
                const step = archetypeGroup.steps.find((s) => s.stepConfigId === dragged.stepConfigIds[0]);
                if (step) return step;
            }
        }
        return null;
    }, [isDraggingStep, dragged, displayedGraph]);

    const draggedStepDestinations = React.useMemo(
        () =>
            (draggedStep?.destinationStepConfigIds ?? [])
                .map((destinationStepConfigId) => destinationInfoByDestinationStepConfigId[destinationStepConfigId])
                .filter((destination) => destination !== undefined),
        [draggedStep, destinationInfoByDestinationStepConfigId],
    );

    const draggedArchetypeGroup = React.useMemo(() => {
        if (dragged?.kind !== 'group' || !displayedGraph) return null;

        for (const stepGroup of displayedGraph.stepGroups) {
            for (const archetypeGroup of stepGroup.archetypeGroups) {
                if (archetypeGroup.steps.some((step) => dragged.stepConfigIds.includes(step.stepConfigId))) {
                    return archetypeGroup;
                }
            }
        }
        return null;
    }, [dragged, displayedGraph]);

    // Read from state rather than the drag event, which loses its payload when a draggable remounts.
    const onDragEnd = ({ over }: DragEndEvent) => {
        setDragged(null);
        if (!over || !displayedGraph || !dragged) return;
        setReorderGraph(moveStepsInGraph(displayedGraph, dragged.stepConfigIds, parseDropTargetId(String(over.id))));
    };

    // Dropping a step back where it started leaves the order untouched, so there is nothing to save.
    const hasOrderChanged = React.useMemo(
        () =>
            !!reorderGraph &&
            !!workflowSteps &&
            JSON.stringify(stepGroupsFromGraph(reorderGraph)) !== JSON.stringify(stepGroupsFromGraph(workflowSteps)),
        [reorderGraph, workflowSteps],
    );

    const [updateStepGraph, isSavingOrder, saveOrderResponseType, saveOrderError] = useCallUpdatable(
        WorkflowUpdatable.UpdateStepGraph,
        { orgId, workflowId },
    );

    const didHandleSaveOrderSuccessRef = React.useRef(false);
    React.useEffect(() => {
        const didSucceed = saveOrderResponseType === RunwayResponseType.Success;
        if (didSucceed && !didHandleSaveOrderSuccessRef.current) {
            didHandleSaveOrderSuccessRef.current = true;
            globalMessage.success('Step order updated');
            setReorderGraph(null);
        }
        if (!didSucceed) {
            didHandleSaveOrderSuccessRef.current = false;
        }
    }, [saveOrderResponseType]);

    React.useEffect(() => {
        if (saveOrderError) {
            globalMessage.error(saveOrderError);
        }
    }, [saveOrderError]);

    const [renameArchetypeGroup, isRenaming, renameResponseType, renameError] = useCallUpdatable(
        WorkflowUpdatable.RenameStepArchetypeGroup,
        {
            orgId,
            workflowId,
        },
    );

    React.useEffect(() => {
        if (!isRenaming) setRenamingGroupKey(null);
    }, [isRenaming]);

    const didHandleRenameSuccessRef = React.useRef(false);
    React.useEffect(() => {
        const didSucceed = renameResponseType === RunwayResponseType.Success;
        if (didSucceed && !didHandleRenameSuccessRef.current) {
            didHandleRenameSuccessRef.current = true;
            globalMessage.success('Step group name updated');
        }
        if (!didSucceed) {
            didHandleRenameSuccessRef.current = false;
        }
    }, [renameResponseType]);

    React.useEffect(() => {
        if (renameError) {
            globalMessage.error(renameError);
        }
    }, [renameError]);

    const selectedStep = React.useMemo(() => {
        if (!selectedStepConfigId || !workflowSteps) return null;

        for (const stepGroup of workflowSteps.stepGroups) {
            for (const archetypeGroup of stepGroup.archetypeGroups) {
                const step = archetypeGroup.steps.find((s) => s.stepConfigId === selectedStepConfigId);
                if (step) {
                    return { step, archetype: archetypeGroup.archetype };
                }
            }
        }
        return null;
    }, [selectedStepConfigId, workflowSteps]);

    return (
        <>
            <Flex vertical gap={32} style={{ width: '100%' }}>
                <DestinationsSettings appId={appId} orgId={orgId} />

                <div ref={stepsContainerRef}>
                    <Flex justify="flex-end" align="center" gap={16} style={{ marginBottom: 16 }}>
                        {hiddenStepCount > 0 && !reorderGraph && (
                            <Space size={8}>
                                <Switch size="small" checked={showHiddenSteps} onChange={setShowHiddenSteps} />
                                <Text style={{ color: antGrey.grey7 }}>
                                    {`Show ${hiddenStepCount} hidden step${hiddenStepCount > 1 ? 's' : ''}`}
                                </Text>
                                <TooltipInfoIcon
                                    tooltipContent={
                                        <span>
                                            {'Toggle showing steps that are '}
                                            <Link
                                                to={createAppSettingsRouteWithSettingType(
                                                    orgId,
                                                    appId,
                                                    SettingType.General,
                                                )}
                                                style={{
                                                    color: 'white',
                                                    textDecorationLine: 'underline',
                                                    textDecorationStyle: 'dotted',
                                                    textUnderlineOffset: 3,
                                                }}
                                            >
                                                configured
                                            </Link>
                                            {' to be non-blocking and hidden.'}
                                        </span>
                                    }
                                />
                            </Space>
                        )}
                        {reorderGraph ?
                            <Space>
                                <Button disabled={isSavingOrder} onClick={() => setReorderGraph(null)}>
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    loading={isSavingOrder}
                                    disabled={!hasOrderChanged}
                                    onClick={() => updateStepGraph({ stepGroups: stepGroupsFromGraph(reorderGraph) })}
                                >
                                    Save order
                                </Button>
                            </Space>
                        :   <PermissionedActionButton
                                actionType={RunwayUserAction.UAUpdateAppSettings}
                                configFileSection={ConfigFileSection.Flightpath}
                                icon={<MenuOutlined />}
                                disabled={!workflowSteps}
                                onClick={() => setReorderGraph(workflowSteps)}
                            >
                                Reorder steps
                            </PermissionedActionButton>
                        }
                    </Flex>

                    <DndContext
                        sensors={dragSensors}
                        onDragStart={({ active }: DragStartEvent) => {
                            setSectionWidth(stepsContainerRef.current?.offsetWidth ?? 0);
                            setDragged(active.data.current as DraggedSteps);
                        }}
                        onDragEnd={onDragEnd}
                        onDragCancel={() => setDragged(null)}
                    >
                        {displayedGraph?.stepGroups.map((stepGroup, groupIndex) => (
                            <div key={groupIndex} style={{ width: '100%' }}>
                                {stepGroup.archetypeGroups.map((archetypeGroup, archetypeIndex) => {
                                    const isHiddenGroup = isStepHiddenFromTimeline(archetypeGroup.archetype);
                                    if (!areHiddenStepsVisible && isHiddenGroup) return null;

                                    const withHiddenMarker = (label: React.ReactNode) =>
                                        !isHiddenGroup ? label : (
                                            <Space size={6}>
                                                {label}
                                                <Tooltip title="Hidden from the release timeline">
                                                    <EyeInvisibleOutlined style={{ color: antGrey.grey6 }} />
                                                </Tooltip>
                                            </Space>
                                        );

                                    const groupKey = `${groupIndex}-${archetypeIndex}`;
                                    const isRenameableGroup =
                                        !reorderGraph &&
                                        canRenameGroups &&
                                        (archetypeGroup.steps.length > 1 || !!archetypeGroup.name);
                                    const isManageableArchetype = manageableWorkflowSteps.some(
                                        (option) => option.archetype === archetypeGroup.archetype,
                                    );

                                    // Landing back in a group that is losing all of its steps changes nothing, and
                                    // the group is about to be pruned anyway.
                                    const holdsOnlyDraggedSteps =
                                        !!dragged &&
                                        archetypeGroup.steps.every((step) =>
                                            dragged.stepConfigIds.includes(step.stepConfigId),
                                        );
                                    if (holdsOnlyDraggedSteps) return null;

                                    const acceptsDraggedStep =
                                        isDraggingStep && dragged.archetype === archetypeGroup.archetype;

                                    return (
                                        <div key={groupKey}>
                                            <WorkflowStepsSection
                                                platform={platform}
                                                appStoreType={appStoreType}
                                                archetypeGroup={archetypeGroup}
                                                onEditClicked={onEditClicked}
                                                destinationInfoByDestinationStepConfigId={
                                                    destinationInfoByDestinationStepConfigId
                                                }
                                                canEditSettings={workflowEnabledSteps[archetypeGroup.archetype]}
                                                isGroupLabelMuted={isHiddenGroup}
                                                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                                platformLabelOverride={mdmOverride?.label}
                                                platformIconOverride={mdmOverride?.icon}
                                                marginBottom={0}
                                                renderGroupLabel={
                                                    reorderGraph && isManageableArchetype ?
                                                        (label) => (
                                                            <DraggableGroupLabel
                                                                id={`group-${groupKey}`}
                                                                dragged={{
                                                                    kind: 'group',
                                                                    stepConfigIds: archetypeGroup.steps.map(
                                                                        (step) => step.stepConfigId,
                                                                    ),
                                                                    archetype: archetypeGroup.archetype,
                                                                }}
                                                            >
                                                                {withHiddenMarker(label)}
                                                            </DraggableGroupLabel>
                                                        )
                                                    : isHiddenGroup ?
                                                        withHiddenMarker
                                                    :   undefined
                                                }
                                                addStepDisabledReason={
                                                    reorderGraph ?
                                                        'Steps cannot be added while reordering. Cancel or save your changes first.'
                                                    :   undefined
                                                }
                                                onAddStep={
                                                    isManageableArchetype ?
                                                        () =>
                                                            setAddStepTarget({
                                                                stepGroupIndex: groupIndex,
                                                                archetypeGroupIndex: archetypeIndex,
                                                                insertAsNewArchetypeGroup: false,
                                                                lockedArchetype: archetypeGroup.archetype,
                                                            })
                                                    :   undefined
                                                }
                                                onRenameGroup={
                                                    isRenameableGroup ?
                                                        (name) => {
                                                            setRenamingGroupKey(groupKey);
                                                            renameArchetypeGroup({
                                                                stepGroupIndex: groupIndex,
                                                                archetypeGroupIndex: archetypeIndex,
                                                                name,
                                                            });
                                                        }
                                                    :   undefined
                                                }
                                                isRenamingGroup={renamingGroupKey === groupKey}
                                                isDisabled={!!reorderGraph && !isManageableArchetype}
                                                renderStepAction={
                                                    reorderGraph ?
                                                        () => <StepDragHandle isHidden={!isManageableArchetype} />
                                                    :   undefined
                                                }
                                                renderStepRow={
                                                    reorderGraph && isManageableArchetype ?
                                                        (step, row) => (
                                                            <DraggableStepRow
                                                                key={step.stepConfigId}
                                                                stepConfigId={step.stepConfigId}
                                                                dragged={{
                                                                    kind: 'step',
                                                                    stepConfigIds: [step.stepConfigId],
                                                                    archetype: archetypeGroup.archetype,
                                                                }}
                                                                dropTarget={
                                                                    acceptsDraggedStep ?
                                                                        {
                                                                            stepGroupIndex: groupIndex,
                                                                            archetypeGroupIndex: archetypeIndex,
                                                                            asNewArchetypeGroup: false,
                                                                            orderIndex: archetypeGroup.steps.findIndex(
                                                                                (candidate) =>
                                                                                    candidate.stepConfigId ===
                                                                                    step.stepConfigId,
                                                                            ),
                                                                        }
                                                                    :   undefined
                                                                }
                                                            >
                                                                {row}
                                                            </DraggableStepRow>
                                                        )
                                                    :   undefined
                                                }
                                                addStepRowOverride={
                                                    acceptsDraggedStep ?
                                                        <StepDropZone
                                                            target={{
                                                                stepGroupIndex: groupIndex,
                                                                archetypeGroupIndex: archetypeIndex,
                                                                asNewArchetypeGroup: false,
                                                            }}
                                                            label="Move into this group"
                                                        />
                                                    :   undefined
                                                }
                                            />
                                            <div style={{ display: 'flex', padding: '16px 0 16px 24px' }}>
                                                {dragged ?
                                                    <div style={{ flex: 1 }}>
                                                        <StepDropZone
                                                            target={{
                                                                stepGroupIndex: groupIndex,
                                                                archetypeGroupIndex: archetypeIndex,
                                                                asNewArchetypeGroup: true,
                                                            }}
                                                            label="Move into new group"
                                                        />
                                                    </div>
                                                :   <Tooltip
                                                        title={
                                                            reorderGraph ?
                                                                'Steps cannot be added while reordering. Cancel or save your changes first.'
                                                            :   undefined
                                                        }
                                                    >
                                                        <PermissionedActionButton
                                                            actionType={RunwayUserAction.UAUpdateAppSettings}
                                                            configFileSection={ConfigFileSection.Flightpath}
                                                            type="link"
                                                            size="small"
                                                            icon={<PlusOutlined />}
                                                            disabled={!!reorderGraph}
                                                            style={{ color: antGrey.grey6, paddingLeft: 0 }}
                                                            className="add-workflow-step"
                                                            onClick={() =>
                                                                setAddStepTarget({
                                                                    stepGroupIndex: groupIndex,
                                                                    archetypeGroupIndex: archetypeIndex,
                                                                    insertAsNewArchetypeGroup: true,
                                                                })
                                                            }
                                                        >
                                                            Add step
                                                        </PermissionedActionButton>
                                                    </Tooltip>
                                                }
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                        <DragOverlay>
                            {draggedArchetypeGroup ?
                                <GroupDragPreview width={sectionWidth}>
                                    <WorkflowStepsSection
                                        platform={platform}
                                        appStoreType={appStoreType}
                                        archetypeGroup={draggedArchetypeGroup}
                                        onEditClicked={() => undefined}
                                        destinationInfoByDestinationStepConfigId={
                                            destinationInfoByDestinationStepConfigId
                                        }
                                        canEditSettings={false}
                                        renderStepAction={() => <StepDragHandle />}
                                        isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                        platformLabelOverride={mdmOverride?.label}
                                        platformIconOverride={mdmOverride?.icon}
                                        marginBottom={0}
                                    />
                                </GroupDragPreview>
                            : draggedStep ?
                                <StepDragPreview
                                    stepName={draggedStep.stepName}
                                    destinations={draggedStepDestinations}
                                />
                            :   null}
                        </DragOverlay>
                    </DndContext>
                </div>
            </Flex>

            {addStepTarget && (
                <AddStepModal
                    appId={appId}
                    orgId={orgId}
                    isOpen
                    onClose={() => setAddStepTarget(null)}
                    stepGroupIndex={addStepTarget.stepGroupIndex}
                    archetypeGroupIndex={addStepTarget.archetypeGroupIndex}
                    insertAsNewArchetypeGroup={addStepTarget.insertAsNewArchetypeGroup}
                    lockedArchetype={addStepTarget.lockedArchetype}
                />
            )}

            <StepConfigSettingsDrawer
                key={selectedStepConfigId}
                appId={appId}
                orgId={orgId}
                stepConfigId={selectedStepConfigId!}
                workflowId={workflowId}
                isOpen={isDrawerOpen}
                releaseStepTypeText={displayTextForReleaseStepType(
                    selectedStep?.archetype,
                    platform,
                    appStoreType,
                    isWorkflowsCIStepEnabled,
                )}
                stepName={selectedStep?.step.stepName}
                onClose={() => {
                    setIsDrawerOpen(false);
                }}
            />
        </>
    );
};
